import { useEffect, useState } from 'react';
import { linkProps } from '../router';
import { loadScLeagues, scApi, setScSettings, useScSettings, type FavTeam, type ScLeague, type TeamInfo } from '../sportcenter';
import { Crest } from './SportCenterBits';

/** Clubs en competities voor het Sportcentrum (per profiel). */
export function SportCenterSettings({ inSettings = false }: { inSettings?: boolean }) {
  const s = useScSettings();
  const [leagues, setLeagues] = useState<ScLeague[]>([]);
  const [q, setQ] = useState('');
  const [browse, setBrowse] = useState('eredivisie');
  const [results, setResults] = useState<TeamInfo[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    void loadScLeagues().then(setLeagues).catch(() => undefined);
  }, []);
  useEffect(() => {
    let off = false;
    const term = q.trim();
    const t = window.setTimeout(() => {
      setBusy(true);
      (term.length >= 2 ? scApi.search(term) : scApi.teams(browse))
        .then((r) => !off && setResults(r.teams))
        .catch(() => !off && setResults([]))
        .finally(() => !off && setBusy(false));
    }, term ? 300 : 0);
    return () => {
      off = true;
      window.clearTimeout(t);
    };
  }, [q, browse]);

  if (!s) return <p className="subtle">Laden…</p>;
  const has = (t: { id: string; path: string }) => s.teams.some((x) => x.id === t.id && x.path === t.path);
  const save = async (b: { teams?: FavTeam[]; leagues?: string[] | null }) => {
    try {
      setScSettings(await scApi.save(b));
      setMsg(null);
    } catch {
      setMsg('Opslaan lukte niet.');
    }
  };
  const toggleTeam = (t: TeamInfo | FavTeam) => void save({
    teams: has(t) ? s.teams.filter((x) => !(x.id === t.id && x.path === t.path)) : [...s.teams, { id: t.id, path: t.path, name: t.name, short: t.short, abbr: t.abbr, logo: t.logo, color: t.color }],
  });
  const move = (i: number, d: number) => {
    const next = [...s.teams];
    const [x] = next.splice(i, 1);
    next.splice(Math.max(0, Math.min(next.length, i + d)), 0, x);
    void save({ teams: next });
  };
  const toggleLeague = (id: string) => void save({ leagues: s.leagues.includes(id) ? s.leagues.filter((x) => x !== id) : [...s.leagues, id] });
  const countries = [...new Set(leagues.map((l) => l.country ?? 'Overig'))];

  return (
    <div className="sc-settings">
      <section className="sc-set-block">
        <h3>Mijn clubs</h3>
        <p className="subtle">Hun wedstrijden staan bovenaan, ook in de beker en Europa. In Live TV krijgen ze een ster. De eerste club is die van de widgets.</p>
        {!s.teams.length && <p className="subtle">Nog geen clubs gekozen.</p>}
        <ul className="sc-myteams" data-focus-group>
          {s.teams.map((t, i) => (
            <li key={`${t.path}|${t.id}`}>
              <Crest logo={t.logo} name={t.short} size={32} />
              <span className="sc-myteam-name">{t.name}</span>
              <button type="button" className="btn btn-sm btn-ghost" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`${t.short} omhoog`}>↑</button>
              <button type="button" className="btn btn-sm btn-ghost" disabled={i === s.teams.length - 1} onClick={() => move(i, 1)} aria-label={`${t.short} omlaag`}>↓</button>
              <button type="button" className="btn btn-sm" onClick={() => toggleTeam(t)}>Verwijderen</button>
            </li>
          ))}
        </ul>
        <div className="sc-find">
          <input className="text-input" type="search" placeholder="Zoek een club (bijv. Ajax, Liverpool, Lakers)" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Zoek een club" />
          {!q.trim() && (
            <select className="chip-select" value={browse} onChange={(e) => setBrowse(e.target.value)} aria-label="Clubs uit competitie">
              {leagues.filter((l) => !['ucl', 'uel', 'uecl', 'knvb-beker', 'facup', 'dfbpokal', 'nationsleague', 'wkq', 'wk', 'ek'].includes(l.id)).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
        </div>
        {busy && !results && <p className="subtle">Zoeken…</p>}
        {results && (
          <div className="sc-team-grid" data-focus-group>
            {results.map((t) => (
              <button key={`${t.path}|${t.id}`} type="button" className={`sc-team-pick${has(t) ? ' is-on' : ''}`} aria-pressed={has(t)} onClick={() => toggleTeam(t)}>
                <Crest logo={t.logo} name={t.short} size={36} />
                <span><b>{t.short}</b><small>{t.leagueName}</small></span>
                <i aria-hidden="true">{has(t) ? '★' : '☆'}</i>
              </button>
            ))}
            {!results.length && <p className="subtle">Niets gevonden.</p>}
          </div>
        )}
      </section>

      <section className="sc-set-block">
        <h3>Competities</h3>
        <p className="subtle">Deze zie je bij Wedstrijden en in de ticker. {s.custom && <button type="button" className="linklike" onClick={() => void save({ leagues: null })}>Terug naar standaard</button>}</p>
        {countries.map((c) => (
          <div key={c} className="sc-league-group">
            <span className="sc-label">{c}</span>
            <div className="chip-wrap" data-focus-group>
              {leagues.filter((l) => (l.country ?? 'Overig') === c).map((l) => (
                <button key={l.id} type="button" className="chip" aria-pressed={s.leagues.includes(l.id)} onClick={() => toggleLeague(l.id)}>{l.name}</button>
              ))}
            </div>
          </div>
        ))}
      </section>
      {msg && <p className="error">{msg}</p>}
      {inSettings && <p><a className="btn btn-sm" {...linkProps('/sportcenter')}>Naar Sport</a></p>}
      <p className="subtle hint">Uitslagen en standen komen van ESPN (gratis, zonder account). Tijdens live wedstrijden ververst alles ongeveer elke 20 seconden.</p>
    </div>
  );
}
